import cventAnalytics from '@cvent/analytics';

import { buildAttendeeActivitiesEnvelope } from './attendeeActivitiesEnvelope';
import { canPublish, getMissingIdentityKeys } from './identity';

import type {
  AttendeeActivitiesFactClient,
  AttendeeActivitiesFactClientConfig,
  CventActivitiesBaseProperties,
  CventActivitiesInitOptions
} from './types';
import type { Action, ActionProps, DestinationNames } from '@cvent/analytics/Action';

import loggerService from '@/services/logger.service';

export type { AttendeeActivitiesFactClient } from './types';

const FACTS_PATH = '/dai/v1/batch_facts';

/**
 * Minimal transport to the Cvent analytics ingestion endpoint — the same
 * `POST {url}/dai/v1/batch_facts` contract the SDK's CventAnalyticsClient
 * uses, but synchronous to construct and free of node builtins (the SDK's
 * client module is not importable from the browser bundle: the package
 * `exports` map blocks deep dist imports, and resolving it via createRequire
 * pulls in node:module).
 *
 * Needed because the SDK's AttendeeActivities destination envelope omits
 * fields the event-bus consumer requires (`account_id`, `environment`,
 * `time`, schema_version `1.0`).
 */
export function createFactClient(config: AttendeeActivitiesFactClientConfig): AttendeeActivitiesFactClient {
  const { url = '', disabled = false, onError } = config;
  return {
    send: (data, callback) => {
      if (disabled) {
        loggerService.log('cvent-analytics: send', data);
        callback?.();
        return;
      }
      fetch(`${url}${FACTS_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([data]),
        keepalive: true
      })
        .then(response => {
          if (!response.ok) {
            onError?.(new Error(`Failed sending fact to cvent-analytics: ${response.statusText}`));
          }
        })
        .catch(error => onError?.(error as Error))
        .finally(() => callback?.());
    }
  };
}

/**
 * Module-level singleton over `@cvent/analytics` for Attendee Activity publishing.
 *
 * It is framework-agnostic on purpose so Vuex actions, socket handlers and plain
 * utilities can publish facts without a Vue context; the composable and plugin
 * that wrap it are WEBINAR-67983. Goldcast's existing Segment analytics are
 * untouched — this pipeline is purely additive and routes only to Cvent.
 */

/** The only destinations Cvent Analytics facts are routed to. */
export const CVENT_ANALYTICS_DESTINATIONS: DestinationNames[] = ['AttendeeActivities'];

declare global {
  /** Set by `@cvent/analytics` once any bundle on the page has initialized it. */
  // eslint-disable-next-line no-var, vars-on-top
  var CVENT_ANALYTICS_INITIALIZED: boolean | undefined;
}

const LOG_PREFIX = 'cvent-analytics:';

type AnalyticsConfig = NonNullable<Parameters<typeof cventAnalytics.init>[0]>;
type GlobalActionProperties = NonNullable<AnalyticsConfig['globalActionProperties']>;

/** Placeholder error reporter used before `init()` supplies a real one. */
const noop = (): void => undefined;

let initialized = false;
let logOnly = false;
let reportError: (error: Error) => void = noop;
let factClient: AttendeeActivitiesFactClient | undefined;
let currentBaseProperties: CventActivitiesBaseProperties | undefined;

/**
 * `@cvent/analytics` types global properties as a fully-populated set of Cvent
 * descriptors. Ours resolve progressively as identity and env config land, so the
 * widening lives at this single boundary rather than forcing callers to invent
 * placeholder values.
 */
function asGlobalActionProperties(properties: Partial<CventActivitiesBaseProperties>): GlobalActionProperties {
  return properties as GlobalActionProperties;
}

function buildErrorReporter(onError?: (error: Error) => void) {
  return (error: Error) => {
    loggerService.error(`${LOG_PREFIX} ${error.message}`, error);
    onError?.(error);
  };
}

/**
 * Initialize the singleton. Idempotent: repeat calls do nothing.
 *
 * Initialization is skipped entirely when any Cvent identifier is missing, which
 * makes the whole pipeline a no-op for Goldcast-native events. Callers own the
 * timing: identity resolves after bootstrap, so `init()` must be called once it
 * is available (`updateBaseProperties()` does not initialize the singleton).
 *
 * `@cvent/analytics.init()` finishes asynchronously but returns `void`, so only
 * synchronous failures reach the `catch` below; anything later surfaces through
 * `onError`, and successful startup is confirmed by the `onReady` log.
 */
export function init(options: CventActivitiesInitOptions): void {
  if (initialized) {
    return;
  }

  const { url, baseProperties, disabled = false, onError, factClientFactory } = options;

  reportError = buildErrorReporter(onError);

  if (!canPublish(baseProperties)) {
    loggerService.log(
      `${LOG_PREFIX} skipping init — incomplete Cvent identity (Goldcast-native event)`,
      getMissingIdentityKeys(baseProperties)
    );
    return;
  }

  if (!url?.trim()) {
    // The library drops every fact without a url, so refuse to look enabled.
    reportError(new Error('missing Cvent activities ingestion url — publishing stays disabled'));
    return;
  }

  if (globalThis.CVENT_ANALYTICS_INITIALIZED) {
    // The library throws asynchronously on a second init, which no caller can
    // catch; refusing here keeps that failure visible and the state honest.
    reportError(new Error('@cvent/analytics was already initialized elsewhere on this page'));
    return;
  }

  try {
    cventAnalytics.init({
      cventAnalytics: { url },
      defaultDestinations: [...CVENT_ANALYTICS_DESTINATIONS],
      globalActionProperties: asGlobalActionProperties(baseProperties),
      // Cvent Activities facts are published explicitly; the library's automatic page-load
      // actions carry no `objectId` and are not part of the fact model.
      pageTracking: false,
      disabled,
      worker: false,
      onError: reportError,
      onReady: () => loggerService.log(`${LOG_PREFIX} ready`)
    });

    initialized = true;
    logOnly = disabled;
    currentBaseProperties = baseProperties;
    factClient = (factClientFactory ?? createFactClient)({ url, disabled, onError: reportError });

    loggerService.log(`${LOG_PREFIX} initialized${disabled ? ' in log-only mode' : ''}`);
  } catch (error) {
    reportError(error as Error);
  }
}

/**
 * Merge properties into the global set applied to every subsequent fact.
 *
 * No-ops before `init()` — nothing is buffered, so late-resolving identity must
 * be supplied through `init()` rather than here.
 */
export function updateBaseProperties(properties: Partial<CventActivitiesBaseProperties>): void {
  if (!initialized) {
    loggerService.log(`${LOG_PREFIX} base property update skipped — not initialized`);
    return;
  }

  try {
    cventAnalytics.updateGlobalActionProperties(asGlobalActionProperties(properties));
    currentBaseProperties = { ...currentBaseProperties, ...properties } as CventActivitiesBaseProperties;
  } catch (error) {
    reportError(error as Error);
  }
}

/**
 * Publish a single Cvent activities fact. Silently no-ops when publishing is not enabled, and
 * logs the fact without publishing in log-only mode.
 */
export function track<A extends ActionProps>(action: Action<A>): void {
  if (!initialized) {
    loggerService.log(`${LOG_PREFIX} track skipped — Cvent activities publishing is not enabled`, action?.id);
    return;
  }

  if (logOnly) {
    loggerService.log(`${LOG_PREFIX} log-only mode — fact not published`, action.id, action.data);
    return;
  }

  try {
    // Send the consumer-shaped envelope (account_id, environment, time,
    // schema_version '1.0') through the SDK's own client. The SDK's
    // AttendeeActivities destination envelope is NOT used: it emits `env`,
    // `ts` only and `account_mapping_id` only, which the event-bus consumer's
    // required-field validation would reject.
    // TODO: https://jira.cvent.com/browse/WEBINAR-70784 - use cvent analytics track method after attendee-activities consumer is fixed
    const envelope = buildAttendeeActivitiesEnvelope(action, currentBaseProperties as CventActivitiesBaseProperties);
    if (factClient) {
      factClient.send(envelope);
    } else {
      reportError(new Error('fact client unavailable — fact dropped'));
    }
  } catch (error) {
    reportError(error as Error);
  }
}

/**
 * Whether the singleton was initialized with a complete Cvent identity.
 *
 * Log-only mode still reports `true`: publishing is wired up and gated call sites
 * should keep producing facts so they can be inspected locally.
 */
export function isEnabled(): boolean {
  return initialized;
}

/** Reset internal state. Tests only. */
export function __resetForTests(): void {
  initialized = false;
  logOnly = false;
  reportError = noop;
  factClient = undefined;
  currentBaseProperties = undefined;
}
